/** Local campaign progress: cleared boards and stars. Swapped for a store interface in WP9. */
export interface LevelProgress {
  cleared: boolean;
  stars: 0 | 1 | 2 | 3;
  bestScore: number;
  attempts: number;
}
/** Owned power-ups for the pre-level picker (Systems Brief §2.2). WP8 adds earning and buying. */
export interface KitStock {
  shield: number;
  turbo: number;
  beam: number;
}
export interface Progress {
  levels: Record<string, LevelProgress>;
  /** Endowed on first launch so the picker is testable before the economy exists (see ENDOWED_KIT). */
  kit?: KitStock;
  /** One-shot hints and reveals already shown on this profile (A4: 'fullLantern'). */
  seen?: Record<string, boolean>;
}

/** Mark a one-shot as seen; true the first time (the caller shows it), false after. */
export function markSeen(p: Progress, key: string): boolean {
  if (!p.seen) p.seen = {};
  if (p.seen[key]) return false;
  p.seen[key] = true;
  saveProgress(p);
  return true;
}
export const ENDOWED_KIT: KitStock = { shield: 2, turbo: 2, beam: 1 };

export function kitStock(p: Progress): KitStock {
  if (!p.kit) p.kit = { ...ENDOWED_KIT };
  return p.kit;
}

/** Spend picked power-ups; returns the picks that were actually in stock. */
export function consumeKit(p: Progress, picks: readonly ('shield' | 'turbo' | 'beam')[]): ('shield' | 'turbo' | 'beam')[] {
  const stock = kitStock(p);
  const used: ('shield' | 'turbo' | 'beam')[] = [];
  for (const k of picks) {
    if (stock[k] > 0) {
      stock[k]--;
      used.push(k);
    }
  }
  saveProgress(p);
  return used;
}

export const PROGRESS_KEY = 'proto.progress.v1';
export const GAUNTLET_BEST_KEY = 'proto.gauntlet.best';
const KEY = PROGRESS_KEY;

export function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as Progress;
  } catch {
    /* ignore */
  }
  return { levels: {} };
}

export function saveProgress(p: Progress): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function levelProgress(p: Progress, boardId: string): LevelProgress {
  return p.levels[boardId] ?? { cleared: false, stars: 0, bestScore: 0, attempts: 0 };
}

/**
 * Stars per Dev Brief v2 §4: 1 for the clear, 2 with no deaths, 3 with an octave or every creature
 * crushed. Returns the merged record (stars and best score never decrease).
 */
export function recordClear(p: Progress, boardId: string, run: { deaths: number; octaves: number; crushKills: number; totalCreatures: number; score: number }): LevelProgress {
  const prev = levelProgress(p, boardId);
  let stars: 0 | 1 | 2 | 3 = 1;
  if (run.deaths === 0) stars = 2;
  if (run.octaves > 0 || (run.totalCreatures > 0 && run.crushKills >= run.totalCreatures)) stars = 3;
  const next: LevelProgress = {
    cleared: true,
    stars: Math.max(prev.stars, stars) as 0 | 1 | 2 | 3,
    bestScore: Math.max(prev.bestScore, run.score),
    attempts: prev.attempts,
  };
  p.levels[boardId] = next;
  saveProgress(p);
  return next;
}

export function recordAttempt(p: Progress, boardId: string): void {
  const prev = levelProgress(p, boardId);
  p.levels[boardId] = { ...prev, attempts: prev.attempts + 1 };
  saveProgress(p);
}

/** Index of the first uncleared board (the glowing "next" lamp), clamped to the last board. */
export function nextLevelIndex(p: Progress, boardIds: string[]): number {
  const i = boardIds.findIndex((id) => !levelProgress(p, id).cleared);
  return i < 0 ? boardIds.length - 1 : i;
}
